import styles from "@/styles/login.module.scss"
import { Button, Form, Input, message, Divider, Checkbox } from 'antd'
import type { CheckboxProps } from 'antd';
import Image from 'next/image'
import { useState } from "react"
import { setCookie } from '@/utils/cookie'
import Link from 'next/link'
import loginApi from '@/service/modules/loginApi'
import { updateUserInfo } from '@/store/slices/userInfoSlice';
import { useDispatch } from "react-redux"
import { useGoogleLoginUtils } from '@/utils/googleLogin'; // 替换为实际的路径
import { matomoSetCustomDimension } from "@/utils/matomoEvent";

interface Props {
  onSwitchType: (type: string) => void; // 通知父组件 请求接口修改数据
  onSuccessJump: () => void; // 通知父组件 请求接口修改数据
}

export default function RegisterForm({ onSwitchType, onSuccessJump }: Props) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  // 校验注册表单成功，发起提交请求
  const onFinishRegisterForm = async(values: any) => {
    try {
      setLoading(true)

      let dataObj = {
        email: values.email.trim(),
        password: window.btoa(values.password),
        password_confirmation: window.btoa(values.password),
        subscribed: checkedWeeklyOffers ? 1 : 0,
        subscribed_crazydeal: checkedDaily ? 1 : 0
      }

      const finnalParams = Object.fromEntries(
        Object.entries(dataObj).filter(([key, value]) => value !== null && value !== '')
      );

      let res = await loginApi.register(finnalParams)
      setLoading(false)
      if (res.data) {
        message.success('Regist Successful!');

        // matomo记录注册结果
        let registerResult = 0
        matomoSetCustomDimension(27, {registerMethod: 'register', registerResult})


        // userEmail存起来，方便数据埋点使用
        if (res.data.email) {
          localStorage.setItem('userEmail', res.data.email || '')
          setCookie('userCustomerId', res.data.customerID || '')
        } else {
          localStorage.removeItem('userEmail')
        }

        // 注册成功，跳转
        if (onSuccessJump) {
          dispatch(updateUserInfo(res.data))
          setTimeout(() => {
            onSuccessJump()
          }, 1500);
        }
      }
    } catch (error: any) {
      setLoading(false)

      if (error.message) {
        if (error.code == 110000) {
          // 已经登录
          const errorMessage = error.message
          const errorField = 'email'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        } else if (error.code == 115003) {
          let currentEmail = localStorage.getItem('userEmail')
          alert(`You are already logged in to your account (${currentEmail}) in another tab.\nPlease log in to the same account in the current tab, or log out of your account (${currentEmail}) and try again.`)
        } else {
          // 其他可能的错误，都放在confirmPassword表单上（因为它是最后一个表单）
          const errorMessage = error.message
          const errorField = 'confirmPassword'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        }
      } else {
        // 如果不是一个有效的数据响应，你可以设置一个通用的错误信息
        message.error('An unexpected error occurred. Please try again.');
      }

      // matomo记录注册结果
      let registerResult = error.code
      matomoSetCustomDimension(27, {registerMethod: 'register', registerResult})
    }
  }

  // 去登录
  const toSignin = async (type: string) => {
    if (onSwitchType) {
      setLoading(false)
      onSwitchType(type)
    }
  }

  const [checkedWeeklyOffers, setCheckedWeeklyOffers] = useState(true);
  const [checkedDaily, setCheckedDaily] = useState(false);

  // 订阅checkbox触发器
  const onChangeWeeklyOffers: CheckboxProps['onChange'] = (e) => {
    setCheckedWeeklyOffers(e.target.checked)
  };
  // 订阅checkbox触发器
  const onChangeDaily: CheckboxProps['onChange'] = (e) => {
    setCheckedDaily(e.target.checked)
  };

  // Google登录
  const onGoogleLogin = useGoogleLoginUtils({ onSuccessJump });

  return (
    <>
      <div className={styles['login-form-box']}>
        <div className={styles['title']}>Create your account</div>
          <Form
            form={form}
            name={'register'}
            layout="vertical"
            size={'large'}
            style={{ width: '100%' }}
            onFinish={onFinishRegisterForm}
            autoComplete="off"
            scrollToFirstError
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: `Email Address is required`},
                { type: 'email', message: `Invalid Email Address`},
              ]}
              getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, '')}
            >
              <Input placeholder="Email Address" maxLength={225} />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: `The password must contain at least 6 characters`},
                { min: 6, message: `The password must contain at least 6 characters` },
              ]}
            >
              <Input.Password placeholder="Password" maxLength={64} />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              rules={[
                { required: true, message: `The password must contain at least 6 characters` },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords are not identical'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm Password" maxLength={64} />
            </Form.Item>

            <div className={styles['choose-subscribe']}>
              <div className={styles['title']}>I prefer to receive:</div>
              <div className={styles['checkout-item']}>
                <Checkbox 
                  checked={checkedWeeklyOffers}
                  onChange={onChangeWeeklyOffers}
                  style={{ marginRight: '10px' }}
                ></Checkbox>
                <div>
                  <div className={styles['checkout-item-title']}>Weekly Offers (3 Times per Week)</div>
                  <div className={styles['checkout-item-desc']}>Free Shipping Offers, Weekly Flash Deals, Birthday Gifts, etc.</div>
                </div>
              </div>

              <div className={`${styles['checkout-item']} margin-top-16`}>
                <Checkbox 
                  checked={checkedDaily}
                  onChange={onChangeDaily}
                  style={{ marginRight: '10px' }}
                ></Checkbox>
                <div>
                  <div className={styles['checkout-item-title']}>Crazy Deal of the Day (Daily)</div>
                  <div className={styles['checkout-item-desc']}>Heavily Reduced Items for 24 Hours</div>
                </div>
              </div>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                className={styles['save-btn']}
                htmlType="submit"
                loading={loading}
              >
                Sign up
              </Button>
            </Form.Item>
          </Form>

          <div className={styles['go-register']}>
            Already have an account?&nbsp;
            <span 
              className={styles['link']}
              onClick={() => {
                toSignin('login')
              }}
            >
              Sign in
            </span>
          </div>

          <Divider style={{ fontSize: '14px', border: '#DDDDDD' }}>OR</Divider>

          <div className={styles['google-login']} onClick={() => onGoogleLogin()}>
            <Image
              alt={'google logo'}
              width={32}
              height={32}
              className={styles['google-icon']}
              src={'https://img.crazysales.com.au/images/Sign-Google.png'} />
            <div className={styles['google-login-text']}>Sign in with Google</div>
          </div>

          <div className={styles['tips']}>
            By creating an account, you agree to be bound by the CrazySales&nbsp;
            <Link
              href={`/review-conditions`}
              legacyBehavior
            >
              <a className={styles['link']} rel="noopener noreferrer">Terms & Conditions</a>
            </Link>
          </div>
        </div>
    </>
  )
}